/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the materials and dispatch tables for one class.
 * @state-owner navigation store owns the sub-tab; dataset store owns materials and runs
 * @allowed-imports React; dataset and workspace stores; shared components and hooks; sibling dialogs
 * @forbidden direct API client; student-only surfaces
 * @public-api ClassDetail
 * @side-effects navigation, dialogs and context menus
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { MenuRows } from '../../../shared/components/ContextMenu';
import { pointerPoint, useOverlay } from '../../../shared/components/OverlayHost';
import { NameCell, StatusText } from '../../../shared/components/StatusIcon';
import { EyeIcon, GearIcon, QrIcon } from '../../../shared/components/VispoIcon';
import {
  CheckCell,
  ToolbarIconButton,
  tableCells,
  VTable,
} from '../../../shared/components/VTable';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { classMaterials, classRuns, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useActivityActions } from '../../activities';
import { useMaterialSelection } from '../store/material-selection';
import { MaterialDeleteDialog } from './MaterialDeleteDialog';
import { MaterialPreviewDialog } from './MaterialPreviewDialog';
import { QrInviteDialog } from './QrInviteDialog';

export function ClassActivitiesTab({ classIndex }: { classIndex: number }) {
  const t = useTranslator();
  const { openModal, openPopover } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const activityTab = useWorkspaceUiStore((state) => state.activityTab);
  const setTab = useWorkspaceUiStore((state) => state.setTab);
  const go = useWorkspaceUiStore((state) => state.go);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  /** Picking materials is a selection, not a navigation: the row toggles a tick. */
  const picked = useMaterialSelection((state) => state.picked);
  const { duplicate } = useActivityActions();
  const toggle = useMaterialSelection((state) => state.toggle);

  const materials = classMaterials(activities, classIndex);
  const dispatches = classRuns(runs, classIndex);
  const tab = activityTab === 'run' ? 'run' : 'mat';

  /** A material belongs to this class, so it opens inside it rather than in the activity library. */
  const openMaterial = (activityId: string) => openItem({ k: 'mat', id: activityId });

  const openRun = (runId: string) => {
    const run = runs.find((item) => item.id === runId);
    if (!run) return;
    if (run.status === 'live') {
      navigate('live', runId);
      return;
    }
    openItem({ k: run.status === 'open' ? 'prog' : 'rev', id: runId });
  };

  return (
    <>
      <div className="fbar" style={{ margin: '0 0 14px' }}>
        <button
          className={`ch ${tab === 'mat' ? 'on' : ''}`}
          type="button"
          onClick={() => setTab('activityTab', 'mat')}
        >
          {t('教材')}
        </button>
        <button
          className={`ch ${tab === 'run' ? 'on' : ''}`}
          type="button"
          onClick={() => setTab('activityTab', 'run')}
        >
          {t('派發')}
        </button>
        <button className="sb" type="button" onClick={() => go('act', null)}>
          {t('教材庫')}
        </button>
      </div>

      {tab === 'mat' ? (
        <VTable
          empty={t('尚未使用任何教材。請至教材庫選取後加入班級。')}
          head={['', t('教材'), t('題數'), t('狀態'), '']}
          rows={materials.map((activity) => ({
            key: activity.id,
            on: picked.includes(activity.id),
            onSelect: () => toggle(activity.id),
            cells: tableCells(
              <CheckCell on={picked.includes(activity.id)} onToggle={() => toggle(activity.id)} />,
              <NameCell icon={t('題字')} text={activity.name} />,
              <span className="vmut">{`${activity.questionCount} ${t('題')}`}</span>,
              activity.status === 'ready' ? (
                <StatusText glyph="done" label={t('可用')} />
              ) : (
                <StatusText glyph="warn" label={t('待確認')} tone="wn" />
              ),
              <>
                <ToolbarIconButton
                  title={t('預覽整份')}
                  onClick={(event) => {
                    event.stopPropagation();
                    openModal(<MaterialPreviewDialog activity={activity} />);
                  }}
                >
                  <EyeIcon />
                </ToolbarIconButton>
                <button
                  className="vcog"
                  style={{ opacity: 1 }}
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const point = pointerPoint(event);
                    openPopover({
                      x: point.x,
                      y: point.y,
                      className: 'cmenu rmenu',
                      content: (
                        <MenuRows
                          items={[
                            { label: t('編輯'), onSelect: () => openMaterial(activity.id) },
                            {
                              label: t('複製一份'),
                              onSelect: () => duplicate(activity.id),
                            },
                            'divider',
                            {
                              label: t('刪除'),
                              danger: true,
                              onSelect: () =>
                                openModal(
                                  <MaterialDeleteDialog
                                    activity={activity}
                                    classIndex={classIndex}
                                  />,
                                ),
                            },
                          ]}
                        />
                      ),
                    });
                  }}
                >
                  <GearIcon />
                </button>
              </>,
            ),
          }))}
          template="36px minmax(0,1fr) 90px 120px 110px"
        />
      ) : (
        <VTable
          empty={t('尚未對此班級發放任何內容。')}
          head={[t('派發'), t('方式'), t('人數'), t('狀態'), '']}
          rows={dispatches.map((run) => ({
            key: run.id,
            dim: run.status === 'ended',
            onSelect: () => openRun(run.id),
            cells: tableCells(
              <span className="vnmc">
                <span className={`vnic ${run.status === 'live' ? 'p' : ''}`}>
                  {run.mode === 'live' ? '課' : t('作')}
                </span>
                <b>{t(run.dateLabel)}</b>
                <span className="vmut" style={{ marginLeft: 8 }}>
                  {runName(activities, run)}
                </span>
              </span>,
              <span className="vmut">{t(RUN_MODE_LABEL[run.mode])}</span>,
              <span className="vmut">
                {`${run.audience === 'some' ? (run.students ?? []).length : classes[classIndex].students.length} 人`}
              </span>,
              run.status === 'live' ? (
                <StatusText glyph="live" label={t('進行中')} tone="pr" />
              ) : run.status === 'open' ? (
                <StatusText glyph="half" label={t('收件中')} />
              ) : (
                <StatusText glyph="done" label={t('已結束')} />
              ),
              run.status === 'ended' ? null : (
                <button
                  className="vqrb on"
                  title={t('這一份的學生連結')}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openModal(<QrInviteDialog id={run.id} kind="run" />);
                  }}
                >
                  <QrIcon />
                </button>
              ),
            ),
          }))}
          template="minmax(0,1fr) 110px 80px 120px 40px"
        />
      )}
    </>
  );
}
